import { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';

// ── Types ─────────────────────────────────────────────────────────────────────

interface EvidenceRow {
  id: string;
  entityType: string;
  entityId: string;
  sourceType: string;
  sourceArtefactType: string | null;
  sourceId: string;
  quoteText: string | null;
  confidence: string;
  createdAt: string;
}

interface EvidenceSummary {
  totalLinks: number;
  requirements: number;
  userStories: number;
  adrs: number;
  issues: number;
  risks: number;
}

interface EntityRow {
  entityId: string;
  entityType: string;
  sources: Set<string>;          // set of sourceArtefactType
  sourceIds: Map<string, string>; // sourceArtefactType → sourceId
}

interface GapInfo {
  entityId: string;
  entityType: string;
  reason: string;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const COLUMNS: { key: string; label: string }[] = [
  { key: 'prd',             label: 'PRD' },
  { key: 'user_stories',    label: 'User Stories' },
  { key: 'sad',             label: 'SAD' },
  { key: 'adr',             label: 'ADRs' },
  { key: 'sprint_plan',     label: 'Sprint Plan' },
  { key: 'sprint_backlog',  label: 'Sprint Backlog' },
  { key: 'handoff_package', label: 'Handoff' },
];

const ENTITY_GROUPS: { type: string; label: string; color: string }[] = [
  { type: 'REQUIREMENT', label: 'Requirements', color: '#7c3aed' },
  { type: 'USER_STORY',  label: 'User Stories',  color: '#10b981' },
  { type: 'ADR',         label: 'ADRs',           color: '#f59e0b' },
  { type: 'RISK',        label: 'Risks',          color: '#ef4444' },
  { type: 'ISSUE',       label: 'Issues',         color: '#3b82f6' },
];

const ENTITY_TYPE_TABS = ['All', 'Requirements', 'User Stories', 'ADRs', 'Risks', 'Issues'];
const TAB_TO_TYPE: Record<string, string> = {
  Requirements: 'REQUIREMENT',
  'User Stories': 'USER_STORY',
  ADRs: 'ADR',
  Risks: 'RISK',
  Issues: 'ISSUE',
};

function sortEntityIds(ids: string[]): string[] {
  return [...ids].sort((a, b) => {
    const numA = parseInt(a.replace(/\D/g, ''), 10) || 0;
    const numB = parseInt(b.replace(/\D/g, ''), 10) || 0;
    return numA - numB || a.localeCompare(b);
  });
}

function detectGaps(grouped: Map<string, EntityRow[]>): GapInfo[] {
  const gaps: GapInfo[] = [];
  for (const row of grouped.get('REQUIREMENT') ?? []) {
    if (!row.sources.has('user_stories')) {
      gaps.push({ entityId: row.entityId, entityType: 'REQUIREMENT', reason: 'No user story covers this requirement' });
    }
  }
  for (const row of grouped.get('USER_STORY') ?? []) {
    if (!row.sources.has('sprint_backlog')) {
      gaps.push({ entityId: row.entityId, entityType: 'USER_STORY', reason: 'Not included in any sprint backlog' });
    }
  }
  for (const row of grouped.get('ADR') ?? []) {
    if (!row.sources.has('sad')) {
      gaps.push({ entityId: row.entityId, entityType: 'ADR', reason: 'ADR not referenced in architecture' });
    }
  }
  return gaps;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function TraceabilityPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  const [evidence, setEvidence] = useState<EvidenceRow[]>([]);
  const [summary, setSummary] = useState<EvidenceSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [building, setBuilding] = useState(false);
  const [buildResult, setBuildResult] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState('All');
  const [showGapsOnly, setShowGapsOnly] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [selectedEntity, setSelectedEntity] = useState<string | null>(null);
  const [tooltip, setTooltip] = useState<{ entityId: string; col: string; x: number; y: number } | null>(null);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    Promise.all([
      apiFetch<EvidenceRow[]>(`/projects/${projectId}/evidence`),
      apiFetch<EvidenceSummary>(`/projects/${projectId}/evidence/summary`),
    ]).then(([ev, sum]) => {
      if (!cancelled) { setEvidence(ev); setSummary(sum); }
    }).catch(() => {}).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId]);

  // ── Derived data ────────────────────────────────────────────────────────────

  const grouped = useMemo<Map<string, EntityRow[]>>(() => {
    const map = new Map<string, EntityRow[]>();
    for (const e of evidence) {
      if (!e.sourceArtefactType) continue;
      const group = map.get(e.entityType) ?? [];
      let row = group.find(r => r.entityId === e.entityId);
      if (!row) {
        row = { entityId: e.entityId, entityType: e.entityType, sources: new Set(), sourceIds: new Map() };
        group.push(row);
        map.set(e.entityType, group);
      }
      row.sources.add(e.sourceArtefactType);
      row.sourceIds.set(e.sourceArtefactType, e.sourceId);
    }
    // sort each group
    for (const [type, rows] of map) {
      map.set(type, sortEntityIds(rows.map(r => r.entityId)).map(id => rows.find(r => r.entityId === id)!));
    }
    return map;
  }, [evidence]);

  const gaps = useMemo(() => detectGaps(grouped), [grouped]);
  const gapSet = useMemo(() => new Set(gaps.map(g => g.entityId)), [gaps]);

  const filteredGroups = useMemo(() => {
    return ENTITY_GROUPS.filter(g => {
      if (selectedTab !== 'All' && TAB_TO_TYPE[selectedTab] !== g.type) return false;
      if (showGapsOnly) {
        const rows = grouped.get(g.type) ?? [];
        return rows.some(r => gapSet.has(r.entityId));
      }
      return (grouped.get(g.type) ?? []).length > 0;
    });
  }, [selectedTab, showGapsOnly, grouped, gapSet]);

  const selectedEntityLinks = useMemo(() => {
    if (!selectedEntity) return [];
    return evidence.filter(e => e.entityId === selectedEntity);
  }, [selectedEntity, evidence]);

  const selectedEntityType = useMemo(() => {
    if (!selectedEntity) return null;
    return evidence.find(e => e.entityId === selectedEntity)?.entityType ?? null;
  }, [selectedEntity, evidence]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleRebuild = async () => {
    if (!projectId) return;
    setBuilding(true);
    setBuildResult(null);
    try {
      const result = await apiFetch<{ totalCreated: number; artefactsScanned: number }>(
        `/projects/${projectId}/build-evidence`, { method: 'POST' }
      );
      setBuildResult(`${result.totalCreated} new links found across ${result.artefactsScanned} artefacts`);
      const [ev, sum] = await Promise.all([
        apiFetch<EvidenceRow[]>(`/projects/${projectId}/evidence`),
        apiFetch<EvidenceSummary>(`/projects/${projectId}/evidence/summary`),
      ]);
      setEvidence(ev);
      setSummary(sum);
    } catch { setBuildResult('Rebuild failed'); }
    finally { setBuilding(false); setTimeout(() => setBuildResult(null), 4000); }
  };

  const toggleGroup = (type: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(type)) { next.delete(type); } else { next.add(type); }
      return next;
    });
  };

  const groupColor = (type: string) => ENTITY_GROUPS.find(g => g.type === type)?.color ?? '#888';

  // ── Styles ───────────────────────────────────────────────────────────────────

  const mainStyle: React.CSSProperties = {
    flex: 1,
    padding: '32px 28px',
    overflowX: 'auto',
    minWidth: 0,
    fontFamily: 'var(--font-body)',
    color: 'var(--text)',
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <main style={mainStyle}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div>
            <Link
              to={`/projects/${projectId}`}
              style={{ fontSize: 13, color: 'var(--text-secondary)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, marginBottom: 8 }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--text)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--text-secondary)'}
            >
              ← Dashboard
            </Link>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Traceability Matrix</h1>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {buildResult && (
              <span style={{ fontSize: 13, color: 'var(--success)', fontWeight: 500 }}>✓ {buildResult}</span>
            )}
            <button
              onClick={() => void handleRebuild()}
              disabled={building}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px',
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)', fontSize: 13, color: 'var(--text)',
                cursor: building ? 'not-allowed' : 'pointer', opacity: building ? 0.6 : 1,
                fontFamily: 'var(--font-body)',
              }}
            >
              <span style={{ display: 'inline-block', animation: building ? 'spin 1s linear infinite' : 'none' }}>🔄</span>
              {building ? 'Rebuilding…' : 'Rebuild Evidence'}
            </button>
          </div>
        </div>

        {/* Summary bar */}
        {summary && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
            padding: '10px 16px', background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)', marginBottom: 16,
          }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginRight: 8 }}>
              {summary.totalLinks} evidence links
            </span>
            {[
              { label: 'Requirements', count: summary.requirements, tab: 'Requirements', color: '#7c3aed' },
              { label: 'User Stories',  count: summary.userStories,  tab: 'User Stories',  color: '#10b981' },
              { label: 'ADRs',          count: summary.adrs,          tab: 'ADRs',          color: '#f59e0b' },
              { label: 'Risks',         count: summary.risks,         tab: 'Risks',         color: '#ef4444' },
              { label: 'Issues',        count: summary.issues,        tab: 'Issues',        color: '#3b82f6' },
            ].map(({ label, count, tab, color }) => count > 0 && (
              <button key={tab} onClick={() => setSelectedTab(tab)} style={{
                padding: '3px 10px', borderRadius: 12, border: `1px solid ${color}33`,
                background: selectedTab === tab ? `${color}22` : 'transparent',
                color, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                fontFamily: 'var(--font-body)',
              }}>
                {count} {label}
              </button>
            ))}
          </div>
        )}

        {/* Gap summary */}
        {gaps.length > 0 && (
          <div style={{
            padding: '10px 14px', marginBottom: 16,
            background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)',
            borderRadius: 'var(--radius-sm)', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <p style={{ fontSize: 13, color: 'var(--warning)', fontWeight: 500, margin: 0 }}>
              ⚠ {gaps.length} gap{gaps.length !== 1 ? 's' : ''} detected:{' '}
              {[
                gaps.filter(g => g.entityType === 'REQUIREMENT').length > 0 && `${gaps.filter(g => g.entityType === 'REQUIREMENT').length} requirement${gaps.filter(g => g.entityType === 'REQUIREMENT').length !== 1 ? 's' : ''} without stories`,
                gaps.filter(g => g.entityType === 'USER_STORY').length > 0 && `${gaps.filter(g => g.entityType === 'USER_STORY').length} stor${gaps.filter(g => g.entityType === 'USER_STORY').length !== 1 ? 'ies' : 'y'} not in sprint`,
                gaps.filter(g => g.entityType === 'ADR').length > 0 && `${gaps.filter(g => g.entityType === 'ADR').length} ADR${gaps.filter(g => g.entityType === 'ADR').length !== 1 ? 's' : ''} without architecture reference`,
              ].filter(Boolean).join(', ')}
            </p>
            <button onClick={() => setShowGapsOnly(v => !v)} style={{
              fontSize: 12, padding: '3px 10px', borderRadius: 'var(--radius-sm)',
              background: showGapsOnly ? 'rgba(245,158,11,0.2)' : 'transparent',
              border: '1px solid rgba(245,158,11,0.4)', color: 'var(--warning)',
              cursor: 'pointer', fontFamily: 'var(--font-body)', whiteSpace: 'nowrap',
            }}>
              {showGapsOnly ? 'Show all' : 'Show gaps only'}
            </button>
          </div>
        )}

        {/* Entity type tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
          {ENTITY_TYPE_TABS.map(tab => (
            <button key={tab} onClick={() => setSelectedTab(tab)} style={{
              padding: '5px 14px', borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              background: selectedTab === tab ? 'var(--primary)' : 'var(--surface)',
              color: selectedTab === tab ? '#fff' : 'var(--text-secondary)',
              fontSize: 13, fontWeight: selectedTab === tab ? 600 : 400,
              cursor: 'pointer', fontFamily: 'var(--font-body)',
            }}>
              {tab}
            </button>
          ))}
        </div>

        {/* Main content — matrix + detail panel */}
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
          {/* Matrix */}
          <div style={{ flex: 1, minWidth: 0, overflowX: 'auto' }}>
            {loading ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 14 }}>
                Loading evidence…
              </div>
            ) : evidence.length === 0 ? (
              <div style={{
                padding: 40, textAlign: 'center', border: '1px dashed var(--border)',
                borderRadius: 'var(--radius)', color: 'var(--text-secondary)',
              }}>
                <p style={{ fontSize: 15, marginBottom: 8 }}>No evidence links yet</p>
                <p style={{ fontSize: 13 }}>Click &quot;Rebuild Evidence&quot; to extract references from artefacts.</p>
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                {/* Column headers */}
                <thead>
                  <tr>
                    <th style={{
                      textAlign: 'left', padding: '8px 12px', fontWeight: 600,
                      color: 'var(--text-secondary)', borderBottom: '2px solid var(--border)',
                      minWidth: 120, position: 'sticky', left: 0, background: 'var(--bg)', zIndex: 1,
                    }}>
                      Entity
                    </th>
                    {COLUMNS.map(col => (
                      <th key={col.key} style={{
                        padding: '8px 8px', fontWeight: 600, textAlign: 'center',
                        color: 'var(--text-secondary)', borderBottom: '2px solid var(--border)',
                        fontSize: 12, whiteSpace: 'nowrap', minWidth: 70,
                      }}>
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredGroups.map(group => {
                    const rows = (grouped.get(group.type) ?? []).filter(r =>
                      !showGapsOnly || gapSet.has(r.entityId)
                    );
                    if (rows.length === 0) return null;
                    const isCollapsed = collapsedGroups.has(group.type);
                    return [
                      // Group header row
                      <tr key={`group-${group.type}`}>
                        <td
                          colSpan={COLUMNS.length + 1}
                          onClick={() => toggleGroup(group.type)}
                          style={{
                            padding: '8px 12px', fontWeight: 700, fontSize: 12,
                            color: group.color, cursor: 'pointer',
                            background: `${group.color}10`,
                            borderTop: '1px solid var(--border)',
                            userSelect: 'none',
                          }}
                        >
                          {isCollapsed ? '▸' : '▾'} {group.label} ({rows.length})
                        </td>
                      </tr>,
                      // Entity rows
                      ...(!isCollapsed ? rows.map(row => {
                        const isGap = gapSet.has(row.entityId);
                        const isSelected = selectedEntity === row.entityId;
                        return (
                          <tr
                            key={row.entityId}
                            onClick={() => setSelectedEntity(isSelected ? null : row.entityId)}
                            style={{
                              cursor: 'pointer',
                              background: isSelected
                                ? `${group.color}15`
                                : isGap
                                ? 'rgba(245,158,11,0.05)'
                                : 'transparent',
                              borderLeft: isGap ? '3px solid rgba(245,158,11,0.5)' : '3px solid transparent',
                            }}
                          >
                            {/* Entity ID cell */}
                            <td style={{
                              padding: '6px 12px', fontFamily: 'var(--font-mono, monospace)',
                              fontSize: 12, color: isGap ? 'var(--warning)' : group.color,
                              fontWeight: 600, borderBottom: '1px solid var(--border)',
                              position: 'sticky', left: 0,
                              background: isSelected
                                ? `${group.color}15`
                                : isGap ? 'rgba(245,158,11,0.05)' : 'var(--bg)',
                              whiteSpace: 'nowrap',
                            }}>
                              {row.entityId}
                              {isGap && (
                                <span
                                  title={gaps.find(g => g.entityId === row.entityId)?.reason}
                                  style={{ marginLeft: 6, fontSize: 11, cursor: 'help' }}
                                >⚠</span>
                              )}
                            </td>
                            {/* Dot cells */}
                            {COLUMNS.map(col => {
                              const hasLink = row.sources.has(col.key);
                              const sourceId = row.sourceIds.get(col.key);
                              return (
                                <td
                                  key={col.key}
                                  style={{
                                    textAlign: 'center', padding: '6px 8px',
                                    borderBottom: '1px solid var(--border)',
                                  }}
                                  onMouseEnter={e => {
                                    if (hasLink && sourceId) {
                                      const rect = (e.target as HTMLElement).getBoundingClientRect();
                                      setTooltip({ entityId: row.entityId, col: col.label, x: rect.left, y: rect.bottom + 4 });
                                    }
                                  }}
                                  onMouseLeave={() => setTooltip(null)}
                                >
                                  {hasLink ? (
                                    <span style={{
                                      display: 'inline-block', width: 10, height: 10,
                                      borderRadius: '50%', background: group.color,
                                      cursor: 'default',
                                    }} />
                                  ) : (
                                    <span style={{
                                      display: 'inline-block', width: 10, height: 10,
                                      borderRadius: '50%', background: 'var(--border)',
                                      opacity: 0.3,
                                    }} />
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      }) : []),
                    ];
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Detail panel */}
          {selectedEntity && (
            <div style={{
              width: 300, flexShrink: 0,
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius)', padding: '16px',
              position: 'sticky', top: 20,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div>
                  <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {selectedEntityType?.replace('_', ' ')}
                  </p>
                  <p style={{
                    fontSize: 16, fontWeight: 700, fontFamily: 'var(--font-mono, monospace)',
                    color: groupColor(selectedEntityType ?? ''), margin: 0,
                  }}>
                    {selectedEntity}
                  </p>
                </div>
                <button onClick={() => setSelectedEntity(null)} style={{
                  background: 'transparent', border: 'none', color: 'var(--text-secondary)',
                  cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 4,
                }}>×</button>
              </div>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10, fontWeight: 600 }}>
                {selectedEntityLinks.length} evidence link{selectedEntityLinks.length !== 1 ? 's' : ''}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {selectedEntityLinks.map(link => (
                  <div key={link.id} style={{
                    padding: '8px 10px', background: 'var(--bg)',
                    border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                  }}>
                    <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
                      {link.sourceArtefactType?.replace(/_/g, ' ').toUpperCase()}
                    </p>
                    <Link
                      to={`/artefacts?tabs=${link.sourceId}&active=${link.sourceId}`}
                      onClick={e => e.stopPropagation()}
                      style={{
                        fontSize: 11, color: 'var(--primary)', textDecoration: 'none',
                        fontFamily: 'var(--font-mono, monospace)',
                      }}
                    >
                      View artefact →
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Tooltip */}
        {tooltip && (
          <div style={{
            position: 'fixed', left: tooltip.x, top: tooltip.y, zIndex: 9999,
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)', padding: '6px 10px',
            fontSize: 12, color: 'var(--text)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)', pointerEvents: 'none',
          }}>
            <strong>{tooltip.entityId}</strong> referenced in <strong>{tooltip.col}</strong>
          </div>
        )}
    </main>
  );
}
